import type { StateCreator } from 'zustand';
import {
  AD_DAILY_MAX,
  AD_REWARD,
  BOOST_COOLDOWN_MS,
  DEBT_FORGIVE_BONUS,
  REVIVE_REWARD,
} from '../../constants';
import { persist } from '../persist';
import { todayStr } from '../../utils/format';
import type { GameState } from '../types';
import { haptic } from '../../sdk/haptic';

export interface AdSlice {
  boostActive: boolean;
  boostCooldownUntil: number;
  adCount: number;
  adDate: string;
  brokeFired: boolean;

  openAd: () => void;
  completeAd: () => void;
  openBoostAd: () => void;
  completeBoostAd: () => void;
  openDebtAd: () => void;
  completeDebtAd: () => void;
  doReviveAd: () => void;
  completeReviveAd: () => void;
  retryGame: () => void;
}


export const createAdSlice: StateCreator<GameState, [], [], AdSlice> = (set, get) => ({
  boostActive: false,
  boostCooldownUntil: 0,
  adCount: 0,
  adDate: '',
  brokeFired: false,

  openAd: () => {
    const s = get();
    if (!s.tutDone) { get().showToast('튜토리얼 진행 중이오'); return; }
    if (AD_DAILY_MAX - s.adCount <= 0) {
      get().showToast('오늘 청원은 다 쓰셨소이다');
      return;
    }
    set({ showAdModal: true });
  },

  completeAd: () => {
    set({ showAdModal: false });
    haptic('adReward');
    const s = get();
    set({
      adDate: todayStr(),
      adCount: s.adCount + 1,
      cash: s.cash + AD_REWARD,
      stats: { ...s.stats, maxCash: Math.max(s.stats.maxCash, s.cash + AD_REWARD) },
    });
    get().showToast(`사또의 은혜! +${(AD_REWARD / 1e4).toLocaleString('ko-KR')}만냥`);
    persist(get());
  },

  openBoostAd: () => {
    const s = get();
    if (!s.tutDone) { get().showToast('튜토리얼 진행 중이오'); return; }
    if (s.position || s.boostActive) return;
    if (Date.now() < s.boostCooldownUntil) {
      const sec = Math.ceil((s.boostCooldownUntil - Date.now()) / 1000);
      get().showToast(`점쟁이가 쉬는 중이오 (${sec}초)`);
      return;
    }
    set({ showBoostModal: true });
  },

  completeBoostAd: () => {
    set({ showBoostModal: false });
    haptic('adReward');
    set({
      boostActive: true,
      boostCooldownUntil: Date.now() + BOOST_COOLDOWN_MS,
    });
    get().showToast('점쟁이 점지! 다음 물량풀기에 적용');
    persist(get());
  },

  openDebtAd: () => {
    if (get().cash >= 0) return;
    set({ showDebtModal: true });
  },

  completeDebtAd: () => {
    set({ showDebtModal: false });
    haptic('adReward');
    const s = get();
    const debt = -s.cash;
    set({
      cash: DEBT_FORGIVE_BONUS,
      stats: { ...s.stats, maxCash: Math.max(s.stats.maxCash, DEBT_FORGIVE_BONUS) },
    });
    get().showToast(
      `💸 변제 완료! 빚 -${Math.round(debt).toLocaleString('ko-KR')}냥 탕감 + ` +
      `${(DEBT_FORGIVE_BONUS / 1e4).toLocaleString('ko-KR')}만냥`,
    );
    persist(get());
  },

  doReviveAd: () => {
    set({ showLiqScreen: false });
    haptic('adReward');
    const s = get();
    set({
      cash: s.cash + REVIVE_REWARD,
      stats: { ...s.stats, maxCash: Math.max(s.stats.maxCash, s.cash + REVIVE_REWARD) },
    });
    get().showToast(`사채업자 등장! +${(REVIVE_REWARD / 1e4).toLocaleString('ko-KR')}만냥`);
    persist(get());
  },

  completeReviveAd: () => {
    set({ showReviveModal: false });
    haptic('adReward');
    const s = get();
    set({
      cash: s.cash + REVIVE_REWARD,
      brokeFired: false,
      stats: { ...s.stats, maxCash: Math.max(s.stats.maxCash, s.cash + REVIVE_REWARD) },
    });
    get().showToast(`사채업자 등장! +${(REVIVE_REWARD / 1e4).toLocaleString('ko-KR')}만냥`);
    persist(get());
  },

  retryGame: () => {
    const s = get();
    set({ showLiqScreen: false });
    if (s.cash >= 0 && s.cash < 10_000) {
      set({ showReviveModal: true });
    } else if (s.cash < 0) {
      get().showToast('💸 빚을 변제받으시오 (점쟁이/사또 사이)');
    }
  },
});
